import React, {useEffect, useState} from "react";
import {Combobox} from "@headlessui/react";
import {SelectorIcon} from "@heroicons/react/solid";
import FieldHint from "../Field/FieldHint";
import ComboboxOptionList, {Option} from "./ComboboxOptionList";
import Spinner from "../../../Spinner";
import {useDebounce} from "../../../../hooks/useDebounce";
import {concatenate} from "../../../../services/string";

// Component ----

export type ComboboxProps = {
  label?: string
  hint?: string
  options: Array<Option>
  selected?: Option | null
  onSearch?: (searchTerm?: string) => void
  onChange: (option: Option | null) => any
  isLoading?: boolean
  disabled?: boolean
  required?: boolean
}

function SingleCombobox({label, hint, options, selected, onSearch, onChange, isLoading, disabled, required}: ComboboxProps) {
  const [query, setQuery] = useState<string>('')
  const debouncedQuery = useDebounce<string>(query, 500)

  useEffect(() => {
    if(onSearch && debouncedQuery) {
      onSearch(debouncedQuery)
    }
  }, [onSearch, debouncedQuery]);

  const filteredOptions = (!!onSearch || query === '') ? options : options.filter(option => {
    const terms = query.toLowerCase().split(' ').filter(Boolean)
    const optionName = option.name.toLowerCase()

    return terms.every(term => optionName.includes(term))
  })

  return (
    <Combobox as="div" value={selected ?? null} onChange={onChange} nullable={true} multiple={false} disabled={disabled}>

      {/* Field Header */}
      <div className="flex justify-between">
        {/* Label */}
        {!!label &&
          <Combobox.Label className="block text-sm font-medium leading-6 text-gray-700">{label}</Combobox.Label>}

        {/* Hint */}
        {!!hint && <FieldHint>{hint}</FieldHint>}
      </div>

      {/* Container */}
      <div className={concatenate('relative', (!!label || !!hint) && 'mt-1')}>

        {/* Main Input */}
        <Combobox.Input
          className={concatenate(disabled ? 'text-gray-500 cursor-not-allowed' : 'text-gray-900', 'w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-brand sm:text-sm sm:leading-6')}
          onChange={event => setQuery(event.target.value)}
          displayValue={(option?: Option | null) => {
            if (!option) return '';

            return `${option.name}${option.description ? ` - ${option?.description}` : ''}`;
          }}
          required={required}
        />

        {/* Open list button */}
        {!!isLoading ?
          <div className="absolute inset-y-0 right-0 flex items-center px-2 focus:outline-none">
            <Spinner color={Spinner.colors.BRAND} size={Spinner.sizes.X_SMALL}/>
          </div>
          :
          <Combobox.Button
            className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <SelectorIcon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
          </Combobox.Button>
        }

        {filteredOptions.length > 0 && <ComboboxOptionList options={filteredOptions}/>}
      </div>
    </Combobox>
  )
}

export default SingleCombobox;